import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Components
import UpdateForm from "../components/UpdateForm";
import AddTransaction from "../components/AddTransaction";
import TransactionDetails from "../components/TransactionDetails";

// Service
import { RequestService } from "services/RequestService";

const StatusCell = ({ value }) => {
  // Split the value into parts if it contains ":"
  const parts = value?.split(":") || [];
  const stName = parts[0]?.trim() || "";
  const gtStatuses = parts[1]?.trim() || "غير معروف";

  return (
    <MDBox display="flex" flexDirection="column">
      <MDTypography variant="caption" fontWeight="medium" color="text">
        {stName}
      </MDTypography>
      <MDBox display="flex" alignItems="center" mt={0.5}>
        <MDBadge
          badgeContent={gtStatuses}
          color={getStatusColor(gtStatuses)}
          variant="gradient"
          size="sm"
          container
          sx={{
            "& .MuiBadge-badge": {
              textTransform: "none",
              fontSize: "0.75rem",
              padding: "0.45em 0.775em",
              minWidth: "24px",
              height: "24px",
              borderRadius: "12px"
            }
          }}
        />
      </MDBox>
    </MDBox>
  );
};

// StatusCellRenderer component
const StatusCellRenderer = ({ value }) => {
  if (!value || value.length === 0) {
    return <MDBadge badgeContent="غير معروف" color="info" size="sm" />;
  }

  return (
    <MDBox display="flex" flexDirection="column" gap={1}>
      {value.map((gt, index) => (
        <MDBox key={index} display="flex" alignItems="center" gap={1}>
          <MDTypography variant="caption" fontWeight="medium">
            {gt.name}
          </MDTypography>
          <MDBadge
            badgeContent={gt.status}
            color={getStatusColor(gt.status)}
            variant="gradient"
            size="sm"
            sx={{
              "& .MuiBadge-badge": {
                textTransform: "none",
                fontSize: "0.75rem",
                padding: "0.45em 0.775em",
                minWidth: "24px",
                height: "24px",
                borderRadius: "12px"
              }
            }}
          />
        </MDBox>
      ))}
    </MDBox>
  );
};


// PropTypes for StatusCell
StatusCell.propTypes = {
  value: PropTypes.string,
};

// Default props for StatusCell
StatusCell.defaultProps = {
  value: "غير معروف",
};

// PropTypes for StatusCellRenderer
StatusCellRenderer.propTypes = {
  value: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
    })
  ),
};


// Default props for StatusCellRenderer
StatusCellRenderer.defaultProps = {
  value: [],
};


// Helper function to determine status color
const getStatusColor = (status) => {
  const statusLower = status?.toLowerCase();

  if (statusLower.includes("running") || statusLower.includes("نشط")) {
    return "success";
  } else if (statusLower.includes("stopped") || statusLower.includes("متوقف")) {
    return "error";
  } else if (statusLower.includes("pending") || statusLower.includes("معلق")) {
    return "warning";
  }
  return "info";
};

// Add prop types validation
StatusCell.propTypes = {
  value: PropTypes.string,
};

// Set default props
StatusCell.defaultProps = {
  value: "غير معروف",
};

function TransactionsDataTables() {
  const [tableData, setTableData] = useState({
    columns: [
      {
        Header: "#",
        accessor: "counter",
        width: "5%"
      },
      // {
      //   Header: "المعرف",
      //   accessor: "id",
      //   width: "5%",
      // },
      {
        Header: "محطة الطاقة",
        accessor: "powerStationName",
        width: "10%",
      },
      // {
      //   Header: "اسم التوربين",
      //   accessor: "stName",
      //   width: "10%",
      // },
      // {
      //   Header: "قيمة التوربين",
      //   accessor: "stValue",
      //   width: "10%",
      // },
      {
        Header: "الطاقة المولدة",
        accessor: "powerGenerator",
        width: "10%",
      },
      {
        Header: "الهدف",
        accessor: "target",
        width: "10%",
      },
      {
        Header: "ضغط الغاز",
        accessor: "gasPressure",
        width: "10%",
      },
      {
        Header: "تدفق الغاز (م³/ساعة)",
        accessor: "gasVolume",
        width: "10%",
      },
      {
        Header: "التردد",
        accessor: "frequency",
        width: "10%",
      },
      {
        Header: "القيمة المتوقعة",
        accessor: "expectedSTValue",
        width: "10%",
      },
      {
        Header: "ST01",
        accessor: "st1Statuses",
        width: "15%",
        Cell: StatusCellRenderer
      },
      {
        Header: "ST02",
        accessor: "st2Statuses",
        width: "15%",
        Cell: StatusCellRenderer
      },
      // {
      //   Header: "المستخدم",
      //   accessor: "userName",
      //   width: "10%",
      // },
      {
        Header: "تاريخ الإنشاء",
        accessor: "createdAt",
        width: "10%",
      },
      {
        Header: "",
        accessor: "actions",
        width: "5%",
      }
    ],
    rows: [],
  });

  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "لوحة ادارة ربان السفينة",
    content: "",
    dateTime: "",
    open: false,
  });

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [updateFormOpen, setUpdateFormOpen] = useState(false);
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalItems: 0
  });
  const [search, setSearch] = useState("");
  const [firstSearch, setFirstSearch] = useState(false);
  const [loading, setLoading] = useState(true);

  const closeNotification = () => setNotification({ ...notification, open: false });

  const handleOpenUpdateDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setUpdateFormOpen(true);
  };

  const handleCloseUpdateDialog = () => {
    setSelectedTransaction(null);
    setUpdateFormOpen(false);
  };

  const handleOpenDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setSelectedTransaction(null);
    setDetailsOpen(false);
  };

  const handleUpdateSuccess = (response) => {
    if (response?.data) {
      fetchTransactions();
      handleCloseUpdateDialog();
      setNotification({
        ...notification,
        color: "success",
        icon: "check",
        title: "لوحة ادارة ربان السفينة",
        content: response?.data?.message || "تم تحديث القراءة بنجاح",
        dateTime: new Date().toLocaleString('ar-IQ'),
        open: true,
      });
    } else {
      setNotification({
        ...notification,
        color: "error",
        icon: "error",
        title: "لوحة ادارة ربان السفينة",
        content: response?.data?.message || "حدث خطأ أثناء تحديث القراءة",
        dateTime: new Date().toLocaleString('ar-IQ'),
        open: true,
      });
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setTransactionToDelete(null);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await RequestService.delete(`Transaction/${transactionToDelete}`);
      if (response.data && !response.data.error) {
        fetchTransactions();
        handleCloseDeleteDialog();
        setNotification({
          ...notification,
          color: "success",
          icon: "check",
          title: "لوحة ادارة ربان السفينة",
          content: response.data?.message || "تم حذف المعاملة بنجاح",
          dateTime: new Date().toLocaleString('ar-IQ'),
          open: true,
        });
      } else {
        setNotification({
          ...notification,
          color: "error",
          icon: "error",
          title: "لوحة ادارة ربان السفينة",
          content: response.data?.message || "حدث خطأ أثناء حذف المعاملة",
          dateTime: new Date().toLocaleString('ar-IQ'),
          open: true,
        });
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
      setNotification({
        ...notification,
        color: "error",
        icon: "error",
        title: "لوحة ادارة ربان السفينة",
        content: error.response?.data?.message || "حدث خطأ أثناء حذف المعاملة",
        dateTime: new Date().toLocaleString('ar-IQ'),
        open: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteConfirmation = (transactionId) => {
    setTransactionToDelete(transactionId);
    setOpenDeleteDialog(true);
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const skip = (pagination.page - 1) * pagination.pageSize;
      const take = pagination.pageSize;

      const response = await RequestService.get(`PowerStation/transactions?searchTerm=${search}&skip=${skip}&take=${take}`);
      if (response.data && !response.data.error) {
        const transactions = response.data.data.transactions || [];

        const formattedTransactions = transactions.map((transaction, index) => {
          // استخراج ST و GT statuses
          const stGtStatuses = {};
          let i = 1;
          transaction.stReadings.forEach((stReading) => {
            stGtStatuses[stReading.stName] = stReading.gtReadings?.map((gt, index) => ({
              name: `GT-${'3' + i++}`,
              status: gt.statusName || "غير معروف",
            })) || [];
          });

          return {
            counter: index + 1 + (pagination.page - 1) * pagination.pageSize,
            id: transaction.id,
            powerStationName: transaction.powerStationName,
            powerGenerator: transaction.powerGenerator.toFixed(2),
            target: transaction.target.toFixed(2),
            gasPressure: transaction.gasPressure.toFixed(2),
            gasVolume: transaction.gasVolume.toFixed(2),
            frequency: transaction.frequency.toFixed(2),
            expectedSTValue: transaction.expectedSTValue?.toFixed(2) || "غير معروف",
            st1Statuses: stGtStatuses["ST-01"] || [],
            st2Statuses: stGtStatuses["ST-02"] || [],
            userName: transaction.userName,
            createdAt: new Date(transaction.createdAt).toLocaleString('ar-IQ'),
            actions: (
              <MDBox display="flex" alignItems="center" gap={1}>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={() => handleOpenDetails(transaction)}
                  startIcon={<Icon>visibility</Icon>}
                >
                  عرض
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={() => handleOpenUpdateDialog(transaction)}
                  startIcon={<Icon>edit</Icon>}
                >
                  تعديل
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleDeleteConfirmation(transaction.id)}
                  startIcon={<Icon>delete</Icon>}
                >
                  حذف
                </MDButton>
              </MDBox>
            ),
          };
        });

        setTableData(prev => ({
          ...prev,
          rows: formattedTransactions,
        }));
        setPagination({
          ...pagination,
          totalItems: response.data.totalCount
        });
      } else {
        setNotification({
          ...notification,
          color: "error",
          icon: "error",
          title: "لوحة ادارة ربان السفينة",
          content: response.data?.message || "حدث خطأ أثناء جلب بيانات المعاملات",
          dateTime: new Date().toLocaleString('ar-IQ'),
          open: true,
        });
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setNotification({
        ...notification,
        color: "error",
        icon: "error",
        title: "لوحة ادارة ربان السفينة",
        content: error.response?.data?.message || "حدث خطأ أثناء جلب بيانات المعاملات",
        dateTime: new Date().toLocaleString('ar-IQ'),
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };


  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handlePageSizeChange = (newSize) => {
    setPagination({ page: 1, pageSize: newSize, totalItems: pagination.totalItems });
  };


  const handleAddTransaction = async (response) => {
    if (response?.data) {
      await fetchTransactions();
      setAddFormOpen(false);
      setNotification({
        ...notification,
        color: "success",
        icon: "check",
        title: "لوحة ادارة ربان السفينة",
        content: response?.data?.message || "تمت إضافة المعاملة بنجاح",
        dateTime: new Date().toLocaleString('ar-IQ'),
        open: true,
      });
    } else {
      setNotification({
        ...notification,
        color: "error",
        icon: "error",
        title: "لوحة ادارة ربان السفينة",
        content: response?.data?.message || "حدث خطأ أثناء إضافة المعاملة",
        dateTime: new Date().toLocaleString('ar-IQ'),
        open: true,
      });
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [pagination.page, pagination.pageSize]);

  useEffect(() => {
    if (search || firstSearch) {
      setFirstSearch(true); {
        setPagination(prev => ({ ...prev, page: 1 }));
        const timer = setTimeout(() => {
          fetchTransactions();
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [search]);
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
                sx={{
                  backgroundColor: "grey.100",
                  borderBottom: "1px solid",
                  borderColor: "grey.300",
                }}
              >
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    لوحة ادارة ربان السفينة
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    ادارة قراءات محطات الطاقة
                  </MDTypography>
                </MDBox>
                <MDBox width="14rem" ml="auto">
                  <MDInput
                    placeholder="بحث ..."
                    value={search}
                    fullWidth
                    onChange={({ currentTarget }) => {
                      setSearch(currentTarget.value);
                    }}
                    sx={{ margin: "0 -10px" }}
                  />
                </MDBox>
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={<Icon>add</Icon>}
                  onClick={() => setAddFormOpen(true)}
                >
                  إضافة القراءة جديدة
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={tableData}
                  isSorted={true}
                  entriesPerPage={{
                    defaultValue: pagination.pageSize,
                    entries: [5, 10, 15, 20, 25],
                    onChange: handlePageSizeChange
                  }}
                  showTotalEntries={true}
                  noEndBorder
                  loading={loading}
                  pagination={{
                    variant: "gradient",
                    color: "info",
                    page: pagination.page,
                    totalPages: Math.ceil(pagination.totalItems / pagination.pageSize),
                    totalItems: pagination.totalItems,
                    onPageChange: handlePageChange
                  }}
                />
              </MDBox>
            </Card>
            <AddTransaction
              open={addFormOpen}
              onClose={() => setAddFormOpen(false)}
              onSuccess={handleAddTransaction}
            />
            {selectedTransaction && (
              <>
                <UpdateForm
                  transaction={selectedTransaction}
                  open={updateFormOpen}
                  onClose={handleCloseUpdateDialog}
                  onSuccess={handleUpdateSuccess}
                />
                <TransactionDetails
                  transaction={selectedTransaction}
                  open={detailsOpen}
                  onClose={handleCloseDetails}
                />
              </>
            )}
            <Dialog
              open={openDeleteDialog}
              onClose={handleCloseDeleteDialog}
              maxWidth="sm"
              fullWidth
              dir="rtl"
            >
              <DialogTitle>
                <MDTypography variant="h6">تأكيد الحذف</MDTypography>
              </DialogTitle>
              <DialogContent>
                <MDTypography variant="body1">
                  هل أنت متأكد من حذف هذه المعاملة؟
                </MDTypography>
              </DialogContent>
              <DialogActions sx={{
                padding: 2,
                display: 'flex',
                justifyContent: 'space-between',
                '& > button': {
                  marginLeft: '8px',
                  marginRight: '0px',
                }
              }}>
                <MDButton onClick={handleDelete} color="error" disabled={isDeleting}>
                  {isDeleting ? "جاري الحذف..." : "حذف"}
                </MDButton>
                <MDButton onClick={handleCloseDeleteDialog} color="secondary">
                  إلغاء
                </MDButton>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        dateTime={notification.dateTime}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
      />
    </DashboardLayout>
  );
}

export default TransactionsDataTables;
